import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";
import { comparisonFilterAtom, type ComparisonFilter } from "../../atoms/reporterComparisonAtoms";
import ComparisonFindingRow from "./ComparisonFindingRow";
import type {
  ComparisonEntry,
  ReporterComparisonResponse,
} from "../../hooks/useReporterComparisonQueries";
import { getClassification, type ActiveClassification } from "../../utils/comparisonClassification";

// ============================================================================
// Filter bar
// ============================================================================

const FILTERS: { value: ComparisonFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "ai_only", label: "AI Only" },
  { value: "agreement", label: "Agreement" },
  { value: "report_only", label: "Report Only" },
];

const FilterBar: React.FC = () => {
  const [filter, setFilter] = useAtom(comparisonFilterAtom);

  return (
    <div className="flex gap-1 px-3 py-2">
      {FILTERS.map(({ value, label }) => (
        <button
          key={value}
          onClick={() => setFilter(value)}
          className={`px-2 py-1 text-[10px] font-medium rounded transition-colors ${
            filter === value ? "bg-primary text-white" : "bg-surface-2 text-fg-muted hover:text-fg"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Helpers
// ============================================================================

function classificationSortOrder(cls: ActiveClassification): number {
  if (cls === "ai_only") return 0;
  if (cls === "agreement") return 1;
  return 2; // report_only
}

function groupByOrgan(entries: ComparisonEntry[]): Record<string, ComparisonEntry[]> {
  const groups: Record<string, ComparisonEntry[]> = {};
  for (const entry of entries) {
    const organ = entry.finding.split("/")[0] ?? "other";
    if (!groups[organ]) groups[organ] = [];
    groups[organ].push(entry);
  }
  return groups;
}

// ============================================================================
// ComparisonPanel
// ============================================================================

interface ComparisonPanelProps {
  data: ReporterComparisonResponse;
  onFindingClick?: (topSliceUid: string) => void;
}

const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ data, onFindingClick }) => {
  const [filter] = useAtom(comparisonFilterAtom);
  const [reportExpanded, setReportExpanded] = useState(false);

  // Classify every entry once. Validation errors surface as Error values; we
  // bail to an error UI below (after all hooks run) rather than throw, since
  // throwing inside useMemo trips React's "rendered fewer hooks" guard before
  // the error boundary can show the real message.
  const classifications = useMemo(
    () => data.comparison_matrix.map((entry) => ({ entry, cls: getClassification(entry) })),
    [data.comparison_matrix],
  );
  const classificationError = useMemo(
    () => classifications.find((c) => c.cls instanceof Error)?.cls as Error | undefined,
    [classifications],
  );

  // Filter + sort comparison matrix
  const filteredEntries = useMemo(() => {
    return classifications
      .filter((item): item is { entry: ComparisonEntry; cls: ActiveClassification } => {
        if (!item.cls || item.cls instanceof Error) return false;
        if (filter === "all") return true;
        return item.cls === filter;
      })
      .sort((a, b) => {
        const orderDiff = classificationSortOrder(a.cls) - classificationSortOrder(b.cls);
        if (orderDiff !== 0) return orderDiff;
        return b.entry.ai.probability - a.entry.ai.probability;
      });
  }, [classifications, filter]);

  const grouped = useMemo(() => {
    return groupByOrgan(filteredEntries.map((e) => e.entry));
  }, [filteredEntries]);

  const classificationMap = useMemo(() => {
    const map = new Map<string, ActiveClassification>();
    for (const { entry, cls } of filteredEntries) {
      map.set(entry.finding, cls);
    }
    return map;
  }, [filteredEntries]);

  // Counts for filter bar context
  const counts = useMemo(() => {
    const c = { ai_only: 0, agreement: 0, report_only: 0 };
    for (const { cls } of classifications) {
      if (cls && !(cls instanceof Error)) c[cls]++;
    }
    return c;
  }, [classifications]);

  const handleFindingClick = (entry: ComparisonEntry) => {
    if (onFindingClick && entry.ai.top_slices.length > 0) {
      onFindingClick(entry.ai.top_slices[0]);
    }
  };

  if (classificationError) {
    return (
      <div className="h-full flex flex-col bg-bg-2 text-fg">
        <div className="shrink-0 px-3 py-2 border-b border-white/10">
          <div className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
            Reporter Comparison
          </div>
        </div>
        <div className="px-3 py-6 text-sm text-warn leading-relaxed">
          <div className="font-medium mb-1">Comparison data invalid</div>
          <div className="text-[11px] text-warn/70 font-mono whitespace-pre-wrap">
            {classificationError.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg-2 text-fg">
      {/* Header */}
      <div className="shrink-0 px-3 py-2 border-b border-white/10">
        <div className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
          Reporter Comparison
        </div>
        <div className="text-[10px] text-fg-muted mt-0.5">
          {counts.ai_only} AI only &middot; {counts.agreement} agree &middot; {counts.report_only}{" "}
          report only
        </div>
      </div>

      {/* Original report — collapsible */}
      <div className="shrink-0 border-b border-white/10">
        <button
          onClick={() => setReportExpanded(!reportExpanded)}
          className="w-full px-3 py-2 text-left text-[11px] font-medium text-fg-muted hover:text-fg transition-colors flex items-center gap-1"
        >
          <span className="text-[10px]">{reportExpanded ? "▼" : "▶"}</span>
          Original Report
        </button>
        {reportExpanded && (
          <div className="px-3 pb-3 max-h-60 overflow-y-auto">
            <pre className="text-[11px] text-fg-muted whitespace-pre-wrap font-sans leading-relaxed">
              {data.original_report_text || "No report text available."}
            </pre>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="shrink-0 border-b border-white/10">
        <FilterBar />
      </div>

      {/* Comparison matrix — scrollable */}
      <div className="flex-1 overflow-y-auto">
        {filteredEntries.length === 0 ? (
          <div className="px-3 py-8 text-center text-fg-muted text-sm">
            No findings match the current filter.
          </div>
        ) : (
          Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([organ, entries]) => (
              <div key={organ}>
                <div className="px-3 py-1.5 bg-surface-2 text-[10px] font-semibold text-fg-muted uppercase tracking-wider sticky top-0">
                  {organ.replace(/_/g, " ")}
                </div>
                <div className="divide-y divide-white/5">
                  {entries.map((entry) => (
                    <ComparisonFindingRow
                      key={entry.finding}
                      entry={entry}
                      classification={classificationMap.get(entry.finding)!}
                      onClick={() => handleFindingClick(entry)}
                    />
                  ))}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default ComparisonPanel;
